// ============================================================================
// EquipesOverviewKpis — bande KPI onglet Apercu (GEST CARTES METIER + TEAM-KPI-WEIGHT)
// ============================================================================
// Le compteur composite equipes/personnes utilise text-body font-semibold (discret)
// au lieu de text-kpi font-bold — echelle nommee, pas le poids KPI plein.

import type { LucideIcon } from 'lucide-react';
import { Users, Smile, Activity, Target } from 'lucide-react';
import { KpiCard } from '@gs/ui';
import { useTranslation } from '../../i18n';
import type { TeamsListKpis } from '../../hooks/useTeams';

function TeamsCountKpi({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: LucideIcon;
}) {
  return (
    <div className="kpi-hover flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="shrink-0 rounded-xl bg-brand-50 p-3 dark:bg-brand-950/40">
        <Icon size={22} className="text-brand-600 dark:text-brand-400" aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <p className="text-meta font-medium text-slate-500 dark:text-slate-400">{label}</p>
        <p className="mt-0.5 text-body font-semibold tabular-nums text-slate-700 dark:text-slate-200">
          {value}
        </p>
      </div>
    </div>
  );
}

/** Moyenne pulse formatee locale — 1 decimale. */
function formatMood(locale: string, value: number): string {
  return new Intl.NumberFormat(locale.startsWith('en') ? 'en-CA' : 'fr-CA', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}

export function EquipesOverviewKpis({ kpis, locale }: { kpis: TeamsListKpis; locale: string }) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <TeamsCountKpi
        icon={Users}
        label={t('gest:equipes.kpis.teams')}
        value={t('gest:equipes.kpis.teamsValue', {
          teams: kpis.teamCount,
          people: kpis.totalHeadcount,
        })}
      />
      <KpiCard
        icon={Smile}
        variant="green"
        label={t('gest:equipes.kpis.mood')}
        value={
          kpis.moodAvg != null
            ? t('gest:equipes.pulse.score', { score: formatMood(locale, kpis.moodAvg) })
            : '—'
        }
      />
      <KpiCard
        icon={Activity}
        variant="blue"
        label={t('gest:equipes.kpis.submitted')}
        value={`${kpis.submittedCount}/${kpis.teamCount}`}
      />
      <KpiCard
        icon={Target}
        variant="brand"
        label={t('gest:equipes.kpis.objectives')}
        value={`${kpis.objectivesDone}/${kpis.objectivesTotal}`}
      />
    </div>
  );
}
